import {useState, useEffect} from 'react';

import {
  type OrganisationMinimalDto,
  type UserAssignedOrganisationsAndGroupsIdsResponse,
} from '@onroadvantage/optix-safeandsound-openapi';

import {organisationApi, userApi} from '../../../api';

interface UseOrganisationDataResult<TSelectedOrgs, TSelectedGroups> {
  organisations: {
    data: TSelectedOrgs | undefined;
    isLoading: boolean;
    error: Error | null;
  };
  organisationGroups: {
    data: TSelectedGroups | undefined;
    isLoading: boolean;
    error: Error | null;
  };
  refetch: () => void;
}

const organisationsCache: {
  data: OrganisationMinimalDto[] | null;
  promise: Promise<OrganisationMinimalDto[]> | null;
  timestamp: number;
} = {data: null, promise: null, timestamp: 0};

const organisationGroupsCache: {
  data: UserAssignedOrganisationsAndGroupsIdsResponse | null;
  promise: Promise<UserAssignedOrganisationsAndGroupsIdsResponse> | null;
  timestamp: number;
} = {data: null, promise: null, timestamp: 0};

let globalRefreshInterval: number | null = null;
let hookInstanceCount = 0;

// 2 minutes
const CACHE_DURATION = 2 * 60 * 1000;

export function useOrganisationData<
  TSelectedOrgs = OrganisationMinimalDto[],
  TSelectedGroups = UserAssignedOrganisationsAndGroupsIdsResponse,
>(
  organisationQueryOptions?: {
    select?: (data: OrganisationMinimalDto[]) => TSelectedOrgs;
  },
  organisationGroupQueryOptions?: {
    select?: (
      data: UserAssignedOrganisationsAndGroupsIdsResponse,
    ) => TSelectedGroups;
  },
): UseOrganisationDataResult<TSelectedOrgs, TSelectedGroups> {
  const [organisations, setOrganisations] = useState<{
    data: TSelectedOrgs | undefined;
    isLoading: boolean;
    error: Error | null;
  }>({
    data: undefined,
    isLoading: true,
    error: null,
  });

  const [organisationGroups, setOrganisationGroups] = useState<{
    data: TSelectedGroups | undefined;
    isLoading: boolean;
    error: Error | null;
  }>({
    data: undefined,
    isLoading: true,
    error: null,
  });

  const isCacheValid = (timestamp: number): boolean => {
    return Date.now() - timestamp < CACHE_DURATION;
  };

  const fetchOrganisations = async (forceRefresh = false): Promise<void> => {
    try {
      setOrganisations((prev) => ({...prev, isLoading: true, error: null}));

      let data: OrganisationMinimalDto[];

      if (
        !forceRefresh &&
        organisationsCache.data &&
        isCacheValid(organisationsCache.timestamp)
      ) {
        data = organisationsCache.data;
      } else if (!forceRefresh && organisationsCache.promise) {
        data = await organisationsCache.promise;
      } else {
        organisationsCache.promise = organisationApi.getMe();
        data = await organisationsCache.promise;
        organisationsCache.data = data;
        organisationsCache.timestamp = Date.now();
      }

      const selectedData = organisationQueryOptions?.select
        ? organisationQueryOptions.select(data)
        : (data as TSelectedOrgs);

      setOrganisations({
        data: selectedData,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      organisationsCache.promise = null;
      setOrganisations({
        data: undefined,
        isLoading: false,
        error: error instanceof Error ? error : new Error('Unknown error'),
      });
    }
  };

  const fetchOrganisationGroups = async (
    forceRefresh = false,
  ): Promise<void> => {
    try {
      setOrganisationGroups((prev) => ({
        ...prev,
        isLoading: true,
        error: null,
      }));

      let data: UserAssignedOrganisationsAndGroupsIdsResponse;

      if (
        !forceRefresh &&
        organisationGroupsCache.data &&
        isCacheValid(organisationGroupsCache.timestamp)
      ) {
        data = organisationGroupsCache.data;
      } else if (!forceRefresh && organisationGroupsCache.promise) {
        data = await organisationGroupsCache.promise;
      } else {
        organisationGroupsCache.promise =
          userApi.getMeOrganisationsAndOrganisationGroupIds();
        data = await organisationGroupsCache.promise;
        organisationGroupsCache.data = data;
        organisationGroupsCache.timestamp = Date.now();
      }

      const selectedData = organisationGroupQueryOptions?.select
        ? organisationGroupQueryOptions.select(data)
        : (data as TSelectedGroups);

      setOrganisationGroups({
        data: selectedData,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      organisationGroupsCache.promise = null;
      setOrganisationGroups({
        data: undefined,
        isLoading: false,
        error: error instanceof Error ? error : new Error('Unknown error'),
      });
    }
  };

  const refetch = (): void => {
    void fetchOrganisations(true);
    void fetchOrganisationGroups(true);
  };

  useEffect(() => {
    hookInstanceCount++;

    void fetchOrganisations();
    void fetchOrganisationGroups();

    globalRefreshInterval ??= window.setInterval(() => {
      void fetchOrganisations(true);
      void fetchOrganisationGroups(true);
    }, CACHE_DURATION);

    return (): void => {
      hookInstanceCount--;

      if (hookInstanceCount === 0 && globalRefreshInterval) {
        clearInterval(globalRefreshInterval);
        globalRefreshInterval = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    organisations,
    organisationGroups,
    refetch,
  };
}
